import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import Modal from "~/common/components/Modal";
import { Button } from "~/common/components/ui/button";
import CheckBox from "~/common/components/ui/CheckBox";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { DIRECTIONS } from "~/common/constants";
import { usaStatesOptions } from "~/common/constants/usaStates";
import { useUpsertPatientPharmacy } from "~/common/hooks/queries/pharmacy";
import { createNewPharmacySchema } from "~/common/schemas/pharmacySchema";
import { PharmacyQueryType } from "~/common/types/pharmacies";
import { CreateNewPharmacyFormValuesType } from "~/common/types/pharmacyForm";
import {
  formatPhoneNumberField,
  getPharmacyDefaultValues,
} from "~/common/utils";

interface PharmacyModalProps {
  show: boolean;
  onClose: () => void;
  selectedPharmacy?: PharmacyQueryType;
  hasDefaultPharmacy?: boolean;
  patientId?: string;
}

export const PharmacyModalForm = ({
  show,
  onClose,
  selectedPharmacy,
  hasDefaultPharmacy = false,
  patientId,
}: PharmacyModalProps) => {
  const mustBeDefault = !selectedPharmacy && !hasDefaultPharmacy;
  const {
    formState: { errors },
    handleSubmit,
    control,
    reset,
    register,
  } = useForm<CreateNewPharmacyFormValuesType>({
    resolver: zodResolver(createNewPharmacySchema),
  });

  const handleCloseModal = () => {
    onClose();
    reset();
  };

  const { upsertPharmacy, isPending } = useUpsertPatientPharmacy(
    handleCloseModal,
    patientId,
  );

  useEffect(() => {
    if (show) {
      reset(
        getPharmacyDefaultValues(selectedPharmacy, {
          forceDefault: mustBeDefault,
        }),
      );
    }
  }, [show, selectedPharmacy, hasDefaultPharmacy, mustBeDefault, reset]);

  return (
    <Modal
      className="max-h-[80vh] w-full overflow-auto"
      title={selectedPharmacy ? "Edit pharmacy" : "Add new pharmacy"}
      show={show}
      onClose={handleCloseModal}
    >
      <form className="flex w-full flex-col gap-4">
        <Input
          id="name"
          label="Pharmacy name"
          {...register("pharmacy.name")}
          error={errors?.pharmacy?.name?.message}
        />

        <Input
          id="phoneNumber"
          label="Phone number"
          {...register("pharmacy.phone_number")}
          {...formatPhoneNumberField<CreateNewPharmacyFormValuesType>(
            "pharmacy.phone_number",
            register,
          )}
          error={errors?.pharmacy?.phone_number?.message}
        />

        <Input
          id="street"
          label="Street address"
          {...register("pharmacy.address.street")}
          error={errors?.pharmacy?.address?.street?.message}
        />

        <div className="flex flex-col gap-4 lg:flex-row">
          <Input
            id="city"
            label="City"
            {...register("pharmacy.address.city")}
            error={errors?.pharmacy?.address?.city?.message}
          />

          <Select
            error={errors?.pharmacy?.address?.state?.message}
            direction={DIRECTIONS.top}
            label="State"
            placeholder="Select state"
            id="pharmacy.address.state"
            options={usaStatesOptions}
            control={control}
            containerClassName="gap-2"
          />

          <Input
            id="zipcode"
            label="ZIP code"
            {...register("pharmacy.address.zipcode")}
            error={errors?.pharmacy?.address?.zipcode?.message}
          />
        </div>

        <div
          className={twMerge(
            "flex w-full flex-col items-center gap-4",
            selectedPharmacy && "justify-end",
          )}
        >
          {!selectedPharmacy && (
            <CheckBox
              className="disabled:cursor-default disabled:bg-gray-300 disabled:hover:bg-gray-300"
              id="is_default"
              {...register("is_default")}
              disabled={mustBeDefault}
            >
              Set as default pharmacy
            </CheckBox>
          )}

          <div className="ml-auto flex w-full flex-col justify-center gap-2 md:w-fit md:flex-row-reverse">
            <Button
              className="h-10 w-full md:w-32"
              onClick={handleSubmit((formData) => {
                upsertPharmacy({
                  formData: {
                    ...formData,
                    is_default: mustBeDefault || formData.is_default,
                  },
                  pharmacyId: selectedPharmacy?.id,
                });
              })}
              isLoading={isPending}
            >
              Save
            </Button>
            <Button
              className="h-10 w-full md:w-32"
              variant="outline"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
